/**
 * gzip compression helpers built on the standard Compression Streams API (`CompressionStream` /
 * `DecompressionStream`), available in evergreen browsers and in Node. The generic persistence
 * layer uses these to shrink a slice's serialized JSON before it is handed to the concurrency
 * store (which encrypts and persists it), and to expand it again on read. The payload stays a
 * `Uint8Array` end to end so it is base64-encoded only once, on upload.
 */

const GZIP_FORMAT = 'gzip';

// Drains a ReadableStream into an ArrayBuffer using the native reader API, avoiding any
// dependency on a Response polyfill (undici's Response and node:stream/web's ReadableStream
// are from different libraries and can fail instanceof checks in Node 22, causing arrayBuffer()
// to never resolve).
async function drain(readable: ReadableStream<Uint8Array>): Promise<ArrayBuffer> {
    const reader = readable.getReader();
    const chunks: Uint8Array[] = [];
    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        chunks.push(value);
    }
    const total = chunks.reduce((n, c) => n + c.byteLength, 0);
    const result = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.byteLength;
    }
    return result.buffer;
}

/**
 * Compresses a string to gzip bytes.
 *
 * @param input the string to compress
 * @returns the gzip-compressed bytes
 */
export async function compressString(input: string): Promise<ArrayBuffer> {
    const readable = new ReadableStream<Uint8Array>({
        start(controller): void {
            controller.enqueue(new TextEncoder().encode(input));
            controller.close();
        },
        // CompressionStream.writable is typed as WritableStream<BufferSource> — wider than
        // Uint8Array — so it doesn't satisfy pipeThrough's ReadableWritablePair constraint.
        // The cast is safe: Uint8Array is always a valid BufferSource.
    }).pipeThrough(new CompressionStream(GZIP_FORMAT) as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
    return drain(readable);
}

/**
 * Decompresses gzip bytes produced by {@link compressString} back to the original string.
 *
 * @param compressed the gzip-compressed bytes
 * @returns the decompressed string
 */
export async function decompressString(compressed: ArrayBuffer): Promise<string> {
    const readable = new ReadableStream<Uint8Array>({
        start(controller): void {
            controller.enqueue(new Uint8Array(compressed));
            controller.close();
        },
    }).pipeThrough(new DecompressionStream(GZIP_FORMAT) as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
    return new TextDecoder().decode(await drain(readable));
}
